import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, Plus, Search } from 'lucide-react';

import { dashboardApi } from '../../services/api';
import type {
    CreateManualAlertInput,
    ManualAlert,
    ManualAlertDetail,
    ManualAlertFilters,
    ManualAlertListResponse,
    ManualAlertSeverity,
    ManualAlertSource,
    ManualAlertStatus,
} from '../../types';
import {
    Button,
    DataToolbar,
    EmptyState,
    ErrorState,
    Input,
    LoadingState,
    LocalPagination,
    Modal,
    ResponsiveDataView,
    SectionPanel,
    Select,
    StatusBadge,
    Textarea,
    useToast,
    SearchSelect,
 } from '../ui';
import { classOptionsByCampus } from '../../utils/pickerOptions';
import { openOnRowClick } from '../../utils/rowClick';
import { getStudentAlertPresentation } from './manualAlertPresentation';
import { ALERT_PAGE_SIZE_OPTIONS, usePagedAlerts } from './usePagedAlerts';

interface ManualAlertBoardProps {
    refreshToken: number;
    // Open + in progress, for the dashboard tab.
    onCount?: (count: number) => void;
}

const statusLabels: Record<ManualAlertStatus, string> = {
    OPEN: 'Cần xử lý',
    IN_PROGRESS: 'Đang xử lý',
    RESOLVED: 'Hoàn tất',
};

const severityLabels: Record<ManualAlertSeverity, string> = {
    HIGH: 'Cao',
    MEDIUM: 'Trung bình',
    LOW: 'Thấp',
};

const sourceLabels: Record<ManualAlertSource, string> = {
    MANUAL: 'Thủ công',
    ATTENDANCE_STUDENT: 'Điểm danh học sinh',
    ATTENDANCE_CLASS: 'Điểm danh buổi học',
};

const statusTones: Record<ManualAlertStatus, 'danger' | 'warning' | 'success'> = {
    OPEN: 'danger',
    IN_PROGRESS: 'warning',
    RESOLVED: 'success',
};

const severityTones: Record<ManualAlertSeverity, 'danger' | 'warning' | 'neutral'> = {
    HIGH: 'danger',
    MEDIUM: 'warning',
    LOW: 'neutral',
};

const activityLabels = {
    CREATED: 'Tạo cảnh báo',
    STATUS_CHANGED: 'Đổi trạng thái',
    NOTE_ADDED: 'Thêm ghi chú',
} as const;

function errorMessage(error: unknown) {
    return error instanceof Error ? error.message : 'Không thể tải cảnh báo.';
}

function studentNames(alert: ManualAlert) {
    if (!alert.students.length) return 'Không gắn học sinh';
    return alert.students
        .map((student) => `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim() || student.id)
        .join(', ');
}

function classLabel(alert: ManualAlert) {
    if (!alert.class) return 'Chưa gắn lớp';
    return alert.class.campus?.name
        ? `${alert.class.name} · ${alert.class.campus.name}`
        : alert.class.name;
}

function activityDetail(metadata: unknown) {
    if (!metadata || typeof metadata !== 'object') return '';
    const values = metadata as Record<string, unknown>;
    if (typeof values.oldStatus === 'string' && typeof values.newStatus === 'string') {
        return `${statusLabels[values.oldStatus as ManualAlertStatus] ?? values.oldStatus} -> ${statusLabels[values.newStatus as ManualAlertStatus] ?? values.newStatus}`;
    }
    return '';
}

function alertDescription(alert: ManualAlert) {
    const presentation = getStudentAlertPresentation(alert);
    if (!presentation) return alert.description;

    const normalizedNotes = presentation.teacherNotes
        .map((note) => note.replace(/\s+/g, ' ').trim())
        .filter(Boolean);

    return presentation.teacherNotes.length
        ? `${presentation.summary}\nGhi chú học sinh: ${normalizedNotes.join(' | ')}`
        : presentation.summary;
}

function getTimestampDisplay(value: string | null) {
    if (!value) return null;

    const timestamp = new Date(value);
    if (Number.isNaN(timestamp.getTime())) {
        return { text: value };
    }

    return {
        text: timestamp.toLocaleString('vi-VN'),
        dateTime: value,
    };
}

function ManualAlertDrawer({
    alertId,
    onClose,
    onChanged,
}: {
    alertId: string | null;
    onClose: () => void;
    onChanged: () => void | Promise<void>;
}) {
    const { showToast } = useToast();
    type StudentDetailItem = {
        label: string;
        value: string;
        rawValue?: string;
    };
    const [detail, setDetail] = React.useState<ManualAlertDetail | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [statusLoading, setStatusLoading] = React.useState(false);
    const [noteLoading, setNoteLoading] = React.useState(false);
    const [note, setNote] = React.useState('');
    const [error, setError] = React.useState('');
    const alertIdRef = React.useRef(alertId);
    alertIdRef.current = alertId;
    const studentPresentation = React.useMemo(
        () => (detail ? getStudentAlertPresentation(detail) : null),
        [detail],
    );
    const studentDetailItems = React.useMemo(() => {
        if (!studentPresentation) return [];

        const checkedAtDisplay = getTimestampDisplay(studentPresentation.checkedAt);
        const lateArrivalDisplay = getTimestampDisplay(studentPresentation.lateArrivalTime);
        const items: Array<StudentDetailItem | null> = [
            studentPresentation.statusLabel
                ? { label: 'Tình trạng điểm danh', value: studentPresentation.statusLabel }
                : null,
            studentPresentation.teacherNotes.length
                ? { label: 'Ghi chú học sinh', value: studentPresentation.teacherNotes.join('\n') }
                : null,
            checkedAtDisplay
                ? {
                    label: 'Thời gian điểm danh',
                    value: checkedAtDisplay.text,
                    rawValue: checkedAtDisplay.dateTime,
                }
                : null,
            lateArrivalDisplay
                ? {
                    label: 'Thời gian đến',
                    value: lateArrivalDisplay.text,
                    rawValue: lateArrivalDisplay.dateTime,
                }
                : null,
        ];

        return items.filter((item): item is StudentDetailItem => Boolean(item));
    }, [studentPresentation]);

    const loadDetail = React.useCallback(async (expectedAlertId = alertId) => {
        if (!expectedAlertId) return;
        const response = await dashboardApi.getManualAlert(expectedAlertId);
        if (alertIdRef.current === expectedAlertId) {
            setDetail(response.data.data);
        }
    }, [alertId]);

    React.useEffect(() => {
        if (!alertId) {
            setDetail(null);
            setNote('');
            setError('');
            return;
        }

        let active = true;
        setDetail(null);
        setNote('');
        setLoading(true);
        setError('');
        setStatusLoading(false);
        setNoteLoading(false);
        dashboardApi.getManualAlert(alertId)
            .then((response) => {
                if (active) setDetail(response.data.data);
            })
            .catch((loadError) => {
                if (active) setError(errorMessage(loadError));
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [alertId]);

    const refreshAfterMutation = async (
        updatedDetail: ManualAlertDetail,
        expectedAlertId: string,
    ) => {
        if (alertIdRef.current !== expectedAlertId) return;
        setDetail((current) => ({
            ...updatedDetail,
            activities: updatedDetail.activities.length
                ? updatedDetail.activities
                : current?.activities ?? [],
        }));
        await Promise.allSettled([
            loadDetail(expectedAlertId),
            Promise.resolve(onChanged()),
        ]);
    };

    const updateStatus = async (status: ManualAlertStatus) => {
        if (!alertId || status === detail?.status) return;
        setStatusLoading(true);
        setError('');
        const expectedAlertId = alertId;
        try {
            const response = await dashboardApi.updateManualAlertStatus(expectedAlertId, status);
            await refreshAfterMutation(response.data.data, expectedAlertId);
            if (alertIdRef.current === expectedAlertId) {
                showToast('Đã cập nhật trạng thái cảnh báo.', 'success');
            }
        } catch (updateError) {
            if (alertIdRef.current === expectedAlertId) {
                setError(errorMessage(updateError));
            }
        } finally {
            if (alertIdRef.current === expectedAlertId) {
                setStatusLoading(false);
            }
        }
    };

    const saveNote = async (event: React.FormEvent) => {
        event.preventDefault();
        const trimmedNote = note.trim();
        if (!alertId || !trimmedNote) return;
        setNoteLoading(true);
        setError('');
        const expectedAlertId = alertId;
        try {
            const response = await dashboardApi.addManualAlertNote(expectedAlertId, trimmedNote);
            if (alertIdRef.current !== expectedAlertId) return;
            setNote('');
            await refreshAfterMutation(response.data.data, expectedAlertId);
            if (alertIdRef.current === expectedAlertId) {
                showToast('Đã lưu ghi chú theo dõi.', 'success');
            }
        } catch (saveError) {
            if (alertIdRef.current === expectedAlertId) {
                setError(errorMessage(saveError));
            }
        } finally {
            if (alertIdRef.current === expectedAlertId) {
                setNoteLoading(false);
            }
        }
    };

    return (
        <Modal
            open={Boolean(alertId)}
            title={detail?.title ?? 'Chi tiết cảnh báo'}
            description={detail ? classLabel(detail) : undefined}
            onClose={onClose}
            className="max-h-[90vh] max-w-5xl overflow-y-auto"
        >
            {loading && !detail ? <LoadingState title="Đang tải chi tiết cảnh báo..." /> : null}
            {error ? <p role="alert" className="mb-4 rounded-lg border border-danger-100 bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</p> : null}
            {detail ? (
                <div className="space-y-6">
                    <SectionPanel className="p-4 sm:p-5">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <p className="text-sm font-medium text-ink-900">
                                    {studentPresentation?.summary ?? detail.description}
                                </p>
                                <p className="mt-2 text-xs text-ink-500">
                                    {sourceLabels[detail.source]} · {studentNames(detail)} · Tạo bởi {detail.creator ? `${detail.creator.firstName} ${detail.creator.lastName}` : 'Hệ thống'}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <StatusBadge tone={severityTones[detail.severity]}>{severityLabels[detail.severity]}</StatusBadge>
                                <StatusBadge tone={statusTones[detail.status]}>{statusLabels[detail.status]}</StatusBadge>
                            </div>
                        </div>
                    </SectionPanel>

                    {studentPresentation ? (
                        <SectionPanel className="p-4 sm:p-5">
                            <div className="mb-4">
                                <h3 className="text-sm font-semibold text-ink-900">Thông tin học sinh</h3>
                            </div>
                            {studentDetailItems.length ? (
                                <dl className="grid gap-4 sm:grid-cols-2">
                                    {studentDetailItems.map((item) => (
                                        <div key={item.label} className="space-y-1">
                                            <dt className="text-xs font-medium text-ink-500">{item.label}</dt>
                                            <dd className="text-sm text-ink-800 whitespace-pre-line">
                                                {item.rawValue ? (
                                                    <time dateTime={item.rawValue}>{item.value}</time>
                                                ) : item.value}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            ) : null}
                        </SectionPanel>
                    ) : null}

                    <SectionPanel className="p-4 sm:p-5">
                        <div className="mb-4">
                            <h3 className="text-sm font-semibold text-ink-900">Theo dõi cảnh báo</h3>
                            <p className="mt-1 text-xs text-ink-500">Cập nhật trạng thái và ghi chú xử lý.</p>
                        </div>
                        <Select
                            label="Trạng thái"
                            value={detail.status}
                            disabled={statusLoading}
                            onChange={(event) => void updateStatus(event.target.value as ManualAlertStatus)}
                        >
                            {Object.entries(statusLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </Select>
                    </SectionPanel>

                    <SectionPanel className="p-4 sm:p-5">
                        <form className="space-y-3" onSubmit={saveNote}>
                            <Textarea
                                id="manual-alert-note"
                                label="Ghi chú theo dõi"
                                value={note}
                                maxLength={2000}
                                rows={4}
                                onChange={(event) => setNote(event.target.value)}
                            />
                            <Button type="submit" loading={noteLoading} disabled={!note.trim()}>
                                Lưu ghi chú
                            </Button>
                        </form>
                    </SectionPanel>

                    <SectionPanel className="p-4 sm:p-5">
                        <h3 className="text-sm font-semibold text-ink-900">Lịch sử theo dõi</h3>
                        <div className="mt-3 space-y-3">
                            {detail.activities.length ? detail.activities.map((activity) => (
                                (() => {
                                    const activityTimestamp = getTimestampDisplay(activity.createdAt);

                                    return (
                                        <div key={activity.id} className="border-l-2 border-ink-200 pl-3">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <p className="text-sm font-medium text-ink-800">{activityLabels[activity.type]}</p>
                                                {activityTimestamp?.dateTime ? (
                                                    <time className="text-xs text-ink-500" dateTime={activityTimestamp.dateTime}>
                                                        {activityTimestamp.text}
                                                    </time>
                                                ) : (
                                                    <span className="text-xs text-ink-500">{activityTimestamp?.text ?? activity.createdAt}</span>
                                                )}
                                            </div>
                                            <p className="mt-1 text-xs text-ink-500">
                                                {activity.actor ? `${activity.actor.firstName} ${activity.actor.lastName}` : 'Hệ thống'}
                                            </p>
                                            {activity.note ? <p className="mt-1 text-sm text-ink-700">{activity.note}</p> : null}
                                            {activityDetail(activity.metadata) ? (
                                                <p className="mt-1 text-sm text-ink-700">{activityDetail(activity.metadata)}</p>
                                            ) : null}
                                        </div>
                                    );
                                })()
                            )) : <p className="text-sm text-ink-500">Chưa có hoạt động theo dõi.</p>}
                        </div>
                    </SectionPanel>
                </div>
            ) : null}
        </Modal>
    );
}

export default function ManualAlertBoard({ refreshToken, onCount }: ManualAlertBoardProps) {
    const { showToast } = useToast();
    const [searchParams] = useSearchParams();
    const [selectedAlertId, setSelectedAlertId] = React.useState<string | null>(null);
    const [createOpen, setCreateOpen] = React.useState(false);
    const [createLoading, setCreateLoading] = React.useState(false);
    const [createError, setCreateError] = React.useState('');
    const [createForm, setCreateForm] = React.useState<CreateManualAlertInput>({
        title: '',
        description: '',
        severity: 'MEDIUM',
        source: 'MANUAL',
        context: {},
    });
    const fetchPage = React.useCallback(
        async (filters: ManualAlertFilters) => (await dashboardApi.getManualAlerts(filters)).data.data as ManualAlertListResponse,
        [],
    );
    const { data, loading, error, filters, setFilter, patchFilters, setPage, searchInput, setSearchInput, reload } = usePagedAlerts(fetchPage, refreshToken);

    React.useEffect(() => {
        if (data && onCount) onCount(data.counts.OPEN + data.counts.IN_PROGRESS);
    }, [data, onCount]);

    React.useEffect(() => {
        const alertId = searchParams.get('manualAlertId');
        if (alertId) setSelectedAlertId(alertId);
    }, [searchParams]);

    const resetCreateForm = () => {
        setCreateForm({
            title: '',
            description: '',
            severity: 'MEDIUM',
            source: 'MANUAL',
            context: {},
        });
        setCreateError('');
    };

    const openCreateModal = () => {
        resetCreateForm();
        setCreateOpen(true);
    };

    const closeCreateModal = () => {
        if (createLoading) return;
        setCreateOpen(false);
        resetCreateForm();
    };

    const updateCreateForm = <K extends keyof CreateManualAlertInput>(
        key: K,
        value: CreateManualAlertInput[K],
    ) => {
        setCreateForm((current) => ({
            ...current,
            [key]: value,
        }));
    };

    const createAlert = async (event: React.FormEvent) => {
        event.preventDefault();
        const payload: CreateManualAlertInput = {
            title: createForm.title.trim(),
            description: createForm.description.trim(),
            severity: createForm.severity,
            source: 'MANUAL',
            ...(createForm.classId ? { classId: createForm.classId } : {}),
            ...(createForm.studentId ? { studentId: createForm.studentId } : {}),
            context: createForm.context ?? {},
        };

        if (!payload.title || !payload.description) return;

        setCreateLoading(true);
        setCreateError('');
        try {
            await dashboardApi.createManualAlert(payload);
            setCreateOpen(false);
            resetCreateForm();
            await reload();
            showToast('Đã tạo cảnh báo.', 'success');
        } catch (createFailure) {
            setCreateError(errorMessage(createFailure));
        } finally {
            setCreateLoading(false);
        }
    };

    const renderStatus = (status: ManualAlertStatus) => (
        <StatusBadge tone={statusTones[status]}>{statusLabels[status]}</StatusBadge>
    );

    const renderSeverity = (severity: ManualAlertSeverity) => (
        <StatusBadge tone={severityTones[severity]}>{severityLabels[severity]}</StatusBadge>
    );

    const open = (alert: ManualAlert) => openOnRowClick(() => setSelectedAlertId(alert.id));
    const items = data?.items ?? [];
    const header = 'px-4 py-3 text-left text-xs font-medium text-ink-500';

    return (
        <div role="region" aria-label="Bảng cảnh báo thủ công" className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-sm text-ink-500">Cảnh báo do giáo viên, trợ giảng hoặc quản lý tạo từ dashboard và điểm danh.</p>
                <Button type="button" size="sm" icon={<Plus className="h-4 w-4" />} onClick={openCreateModal} aria-label="Thêm cảnh báo thủ công">
                    Thêm
                </Button>
            </div>
            <div role="group" aria-label="Bộ lọc cảnh báo cần theo dõi">
                <DataToolbar className="shadow-none sm:items-center" controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
                    <Input
                        aria-label="Tìm kiếm cảnh báo"
                        className="min-h-9 lg:w-72"
                        placeholder="Tiêu đề, mô tả, học sinh..."
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        leftIcon={<Search className="h-4 w-4" />}
                    />
                    <Select
                        aria-label="Lọc theo cơ sở"
                        className="min-h-9 lg:w-44"
                        value={filters.campusId ?? ''}
                        onChange={(event) => patchFilters({ campusId: event.target.value || undefined, classId: undefined })}
                    >
                        <option value="">Tất cả cơ sở</option>
                        {data?.filters.campuses.map((campus) => <option key={campus.id} value={campus.id}>{campus.name}</option>)}
                    </Select>
                    <SearchSelect
                        aria-label="Lọc theo lớp học"
                        className="min-h-9 lg:w-44"
                        value={filters.classId ?? ''}
                        onChange={(value) => setFilter('classId', value)}
                        emptyOption="Tất cả lớp"
                        options={classOptionsByCampus(data?.filters.classes ?? [], data?.filters.campuses ?? [], filters.campusId)}
                    />
                    <Select
                        aria-label="Lọc theo nguồn"
                        className="min-h-9 lg:w-44"
                        value={filters.source ?? ''}
                        onChange={(event) => setFilter('source', event.target.value as ManualAlertSource | '')}
                    >
                        <option value="">Mọi nguồn</option>
                        {Object.entries(sourceLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </Select>
                    <Select
                        aria-label="Lọc theo mức độ"
                        className="min-h-9 lg:w-40"
                        value={filters.severity ?? ''}
                        onChange={(event) => setFilter('severity', event.target.value as ManualAlertSeverity | '')}
                    >
                        <option value="">Mọi mức độ</option>
                        {Object.entries(severityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </Select>
                    <Select
                        aria-label="Lọc theo trạng thái"
                        className="min-h-9 lg:w-56"
                        value={filters.status ?? ''}
                        onChange={(event) => setFilter('status', event.target.value as ManualAlertStatus | '')}
                    >
                        <option value="">Cần xử lý + đang xử lý</option>
                        {(Object.keys(statusLabels) as ManualAlertStatus[]).map((status) => (
                            <option key={status} value={status}>{statusLabels[status]} ({data?.counts[status] ?? 0})</option>
                        ))}
                    </Select>
                </DataToolbar>
            </div>

            {error ? <ErrorState title="Không thể tải cảnh báo" description={error} onRetry={() => void reload()} /> : null}
            {!error && loading && !data ? <LoadingState title="Đang tải cảnh báo..." /> : null}
            {!error && data && !items.length ? (
                <EmptyState
                    title={filters.status ? `Không có cảnh báo ${statusLabels[filters.status].toLowerCase()}` : 'Không có cảnh báo cần theo dõi'}
                    description="Dùng nút Thêm để tạo cảnh báo khi cần xử lý riêng."
                    className="min-h-36"
                />
            ) : null}
            {!error && items.length ? (
                <div className="overflow-hidden rounded-lg border border-ink-200 max-lg:border-0">
                    <ResponsiveDataView
                        desktopLabel="Danh sách cảnh báo cần theo dõi"
                        mobileLabel="Danh sách cảnh báo cần theo dõi (di động)"
                        desktop={(
                            <table className="min-w-full divide-y divide-ink-200 text-sm">
                                <thead className="bg-ink-50">
                                    <tr>
                                        <th className={header}>Cảnh báo</th>
                                        <th className={header}>Lớp / cơ sở</th>
                                        <th className={header}>Học sinh</th>
                                        <th className={header}>Nguồn</th>
                                        <th className={header}>Mức độ</th>
                                        <th className={header}>Trạng thái</th>
                                        <th className={header}><span className="sr-only">Xem</span></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-ink-100 bg-white">
                                    {items.map((alert) => (
                                        <tr
                                            key={alert.id}
                                            aria-label={`Cảnh báo ${alert.title}`}
                                            onClick={open(alert)}
                                            className="cursor-pointer align-top hover:bg-ink-100/60"
                                        >
                                            <td className="max-w-md px-4 py-3">
                                                <p className="font-medium text-ink-900">{alert.title}</p>
                                                <p className="mt-0.5 line-clamp-2 whitespace-pre-line text-xs text-ink-500">{alertDescription(alert)}</p>
                                            </td>
                                            <td className="px-4 py-3 text-ink-700">{classLabel(alert)}</td>
                                            <td className="px-4 py-3 text-ink-700">{studentNames(alert)}</td>
                                            <td className="px-4 py-3 text-ink-600">{sourceLabels[alert.source]}</td>
                                            <td className="px-4 py-3">{renderSeverity(alert.severity)}</td>
                                            <td className="px-4 py-3">{renderStatus(alert.status)}</td>
                                            <td className="px-2 py-3 text-ink-400"><ChevronRight className="h-4 w-4" /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        mobile={items.map((alert) => (
                            <article key={alert.id} onClick={open(alert)} className="cursor-pointer rounded-lg border border-ink-200 bg-white p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h4 className="font-semibold text-ink-900">{alert.title}</h4>
                                        <p className="mt-1 text-sm text-ink-600">{classLabel(alert)}</p>
                                        <p className="mt-1 text-xs text-ink-500">{studentNames(alert)}</p>
                                    </div>
                                    {renderStatus(alert.status)}
                                </div>
                                <p className="mt-3 line-clamp-3 whitespace-pre-line text-sm text-ink-600">{alertDescription(alert)}</p>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    {renderSeverity(alert.severity)}
                                    <span className="text-xs text-ink-500">{sourceLabels[alert.source]}</span>
                                </div>
                            </article>
                        ))}
                    />
                </div>
            ) : null}
            {!error && data ? (
                <LocalPagination
                    label="cảnh báo cần theo dõi"
                    page={data.pagination.page}
                    pageSize={data.pagination.pageSize}
                    totalItems={data.pagination.total}
                    pageSizeOptions={ALERT_PAGE_SIZE_OPTIONS}
                    onPageChange={setPage}
                    onPageSizeChange={(pageSize) => setFilter('pageSize', pageSize)}
                    className="border-t-0 px-0"
                />
            ) : null}

            <ManualAlertDrawer
                key={selectedAlertId ?? 'manual-alert-drawer'}
                alertId={selectedAlertId}
                onClose={() => setSelectedAlertId(null)}
                onChanged={reload}
            />

            <Modal
                open={createOpen}
                title="Thêm cảnh báo"
                description="Tạo cảnh báo thủ công để đội ngũ theo dõi và xử lý."
                onClose={closeCreateModal}
                className="max-w-2xl"
            >
                <form className="space-y-4" onSubmit={createAlert}>
                    {createError ? <p role="alert" className="rounded-lg border border-danger-100 bg-danger-50 px-3 py-2 text-sm text-danger-700">{createError}</p> : null}
                    <Input
                        label="Tiêu đề"
                        value={createForm.title}
                        maxLength={180}
                        onChange={(event) => updateCreateForm('title', event.target.value)}
                    />
                    <Textarea
                        label="Mô tả"
                        value={createForm.description}
                        maxLength={4000}
                        rows={4}
                        onChange={(event) => updateCreateForm('description', event.target.value)}
                    />
                    <div className="grid gap-3 sm:grid-cols-2">
                        <Select
                            label="Mức độ"
                            value={createForm.severity}
                            onChange={(event) => updateCreateForm('severity', event.target.value as ManualAlertSeverity)}
                        >
                            {Object.entries(severityLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </Select>
                        <SearchSelect
                            label="Lớp"
                            value={createForm.classId ?? ''}
                            onChange={(value) => updateCreateForm('classId', value || undefined)}
                            emptyOption="Không gắn lớp"
                            options={classOptionsByCampus(data?.filters.classes ?? [], data?.filters.campuses ?? [])}
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="secondary" onClick={closeCreateModal} disabled={createLoading}>
                            Hủy
                        </Button>
                        <Button
                            type="submit"
                            loading={createLoading}
                            disabled={!createForm.title.trim() || !createForm.description.trim()}
                        >
                            Tạo cảnh báo
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
